import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { TranslateService } from '@ngx-translate/core';
import type { Iso2, Iti } from 'intl-tel-input';
import intlTelInput from 'intl-tel-input';
import { finalize } from 'rxjs/operators';
import { CurrentUserService } from 'src/app/shared/current-user/current-user.service';
import { showHttpErrorResponse } from 'src/app/shared/display-error.helper';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { PHONE_VALIDATION_ERROR_CODES, intlTelInputValidator } from 'src/app/shared/utils/phone-validation.utils';

export interface UserProfileDialogUser {
  email: string;
  firstName: string;
  initials: string;
  lastName: string;
  mobilePhone?: string;
}

export interface UserProfileDialogData {
  secondary: string;
  title: string;
  userProfile: UserProfileDialogUser;
}

@Component({
  selector: 'app-user-profile-dialog',
  templateUrl: './user-profile-dialog.component.html',
  styleUrls: ['./user-profile-dialog.component.scss'],
})
export class UserProfileDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  form: UntypedFormGroup;
  isSaving = false;

  @ViewChild('phoneInput') phoneInputRef: ElementRef<HTMLInputElement>;

  private iti: Iti | null = null;

  constructor(
    public dialogRef: MatDialogRef<UserProfileDialogComponent>,
    private fb: UntypedFormBuilder,
    private translateService: TranslateService,
    private currentUserService: CurrentUserService,
    private notificationService: NotificationService,
    @Inject(MAT_DIALOG_DATA) public data: UserProfileDialogData,
  ) {}

  ngOnInit(): void {
    const p = this.data.userProfile;
    this.form = this.fb.group({
      firstName: [p?.firstName ?? '', [Validators.required]],
      lastName: [p?.lastName ?? '', [Validators.required]],
      mobilePhone: [p?.mobilePhone ?? '', [intlTelInputValidator(() => this.iti)]],
    });
  }

  async ngAfterViewInit(): Promise<void> {
    if (this.phoneInputRef) {
      const phoneInputElement = this.phoneInputRef.nativeElement;
      let initialCountry: Iso2 = 'ca';

      if (!this.data.userProfile?.mobilePhone) {
        initialCountry = await this.getCountryCodeFromIP();
      }

      this.iti = intlTelInput(phoneInputElement, {
        initialCountry,
        separateDialCode: true,
        countryOrder: ['ca', 'us', 'mx'],
        loadUtils: () => import('intl-tel-input/dist/js/utils.js'),
      });

      const initial = this.data.userProfile?.mobilePhone ?? '';
      if (initial) {
        this.iti.setNumber(initial);
      }

      const syncToForm = () => {
        const mobileControl = this.form.get('mobilePhone');
        mobileControl?.setValue(this.iti?.getNumber() ?? '');
        mobileControl?.markAsTouched();
        mobileControl?.updateValueAndValidity({ emitEvent: false });
      };

      phoneInputElement.addEventListener('input', syncToForm);
      phoneInputElement.addEventListener('keyup', syncToForm);
      phoneInputElement.addEventListener('change', syncToForm);
      phoneInputElement.addEventListener('countrychange', syncToForm);
    }
  }

  private async getCountryCodeFromIP(): Promise<Iso2> {
    try {
      const response = await fetch('https://ipapi.co/json');
      const data = await response.json();
      return (data?.country_code?.toLowerCase() ?? 'ca') as Iso2;
    } catch {
      return 'ca';
    }
  }

  ngOnDestroy(): void {
    this.iti?.destroy();
  }

  close(): void {
    this.dialogRef.close();
  }

  save(): void {
    if (!this.form || this.isSaving) {
      return;
    }

    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();

    if (this.form.invalid) {
      return;
    }

    const firstName = (this.form.get('firstName')?.value ?? '').trim();
    const lastName = (this.form.get('lastName')?.value ?? '').trim();
    const normalizedPhone = (this.iti?.getNumber() ?? this.form.get('mobilePhone')?.value ?? '').trim();
    const mobilePhone = normalizedPhone || null;

    this.isSaving = true;
    this.currentUserService
      .updateCurrentUserProfile({
        email: this.data.userProfile?.email ?? '',
        firstName,
        lastName,
        mobilePhone,
      })
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe({
        next: () => {
          this.dialogRef.close({ firstName, lastName, mobilePhone });
        },
        error: (error: HttpErrorResponse) => {
          showHttpErrorResponse(this.notificationService, error, '');
        },
      });
  }

  get initials(): string {
    return this.data.userProfile?.initials ?? '?';
  }

  isFieldInvalid(fieldName: string): boolean {
    const control = this.form?.get(fieldName);
    return !!control && control.invalid && control.touched;
  }

  get phoneErrorMessage(): string {
    const errors = this.form?.get('mobilePhone')?.errors;
    if (!errors?.['phoneInvalid']) return this.translateService.instant('general.phoneInvalid');
    const code = errors['phoneInvalid'].errorCode as string;
    const keyMap: Record<string, string> = {
      [PHONE_VALIDATION_ERROR_CODES.INVALID_COUNTRY_CODE]: 'general.phoneInvalidCountryCode',
      [PHONE_VALIDATION_ERROR_CODES.TOO_SHORT]: 'general.phoneTooShort',
      [PHONE_VALIDATION_ERROR_CODES.TOO_LONG]: 'general.phoneTooLong',
      [PHONE_VALIDATION_ERROR_CODES.IS_POSSIBLE_LOCAL_ONLY]: 'general.phonePossibleLocalOnly',
      [PHONE_VALIDATION_ERROR_CODES.INVALID_LENGTH]: 'general.phoneInvalidLength',
    };
    return this.translateService.instant(keyMap[code] ?? 'general.phoneInvalid');
  }
}
